/* Een locatie verwijderen, maar nooit een waar energiemonitoring aan hangt (F9.12).
 * Dezelfde dialoog in de lijst en op de sitepagina.
 *
 * Alleen "zeker geen energiemonitoring" mag verwijderen: een verwijderde site maakt
 * haar EMS onbereikbaar, en laden of een fout is geen zekerheid. De route zelf
 * (verwijderSite) kijkt vlak vóór het schrijven nog een keer. */

import type { ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { magVerwijderen, ONTKOPPELEN, type EmsKenmerk } from '../data/verwijderen';

/** Waarom de site niet verwijderd kan worden, of null als het wél kan. */
export function verwijderBezwaar(kenmerk: EmsKenmerk | undefined, naam: string): string | null {
  if (magVerwijderen(kenmerk)) return null;
  if (kenmerk?.soort === 'met-ems') {
    return `Aan "${naam}" hangt energiemonitoring. Een verwijderde locatie maakt die onbereikbaar. ${ONTKOPPELEN}`;
  }
  if (kenmerk?.soort === 'fout') {
    return `Of aan "${naam}" energiemonitoring hangt kon niet worden gelezen, dus kan de locatie nu niet worden verwijderd. (${kenmerk.melding})`;
  }
  return `Er wordt nog nagegaan of aan "${naam}" energiemonitoring hangt. Tot dat vaststaat kan de locatie niet worden verwijderd.`;
}

export function VerwijderLocatie({ naam, ems, onVerwijder, trigger }: {
  naam: string;
  ems: EmsKenmerk | undefined;
  onVerwijder: () => void;
  /** Eigen knop; standaard een prullenbak met aria-label "Verwijderen". */
  trigger?: ReactNode;
}) {
  const bezwaar = verwijderBezwaar(ems, naam);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon" aria-label="Verwijderen" className="h-8 w-8 text-muted-foreground hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </AlertDialogTrigger>
      {bezwaar ? (
        <AlertDialogContent data-verwijderen="geweigerd">
          <AlertDialogHeader>
            <AlertDialogTitle>Deze locatie kan niet worden verwijderd</AlertDialogTitle>
            <AlertDialogDescription>{bezwaar}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Sluiten</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      ) : (
        <AlertDialogContent data-verwijderen="bevestigen">
          <AlertDialogHeader>
            <AlertDialogTitle>Locatie verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              "{naam}" verdwijnt uit Locaties. Herstellen kan alleen via Greenvision-beheer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-verwijder-bevestig
              onClick={onVerwijder}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}
